/**
 * FicheCalculMoteur — fiche de calcul barémée PR/PVP (détail moteur pricing.py).
 *
 * Affiche : PR par composante + total, PVP calculé + arrondi + mode (composantes
 * ou coef global) + coefs, marge, prix figé, manques visibles.
 * Source : GET /bijoux/<id>/prix-calcule (lecture seule, cours de fabrication).
 *
 * Réutilisé par CatalogueBijoux (section accordéon) et BijouxAvance (modal détail).
 * - calc/loading fournis par le parent -> pas de fetch (évite le double appel quand
 *   d'autres sections partagent la donnée, cas Catalogue).
 * - sinon auto-fetch au montage (cas BijouxAvance : monté = modal ouvert = lazy).
 *
 * §205 : ne rend RIEN pour une vendeuse (défense en profondeur, en plus du gate parent).
 */
import React, { useState, useEffect } from 'react';
import { API_URL } from '../../services/api';
import { isVendeuse } from '../../utils/typeCaisseLabels';

const headers = () => ({ 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' });

const fmtDH = (v) => (v === null || v === undefined) ? '—' : `${Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} DH`;

export default function FicheCalculMoteur({ bijouId, prixFige, calc: calcExterne, loading: loadingExterne }) {
  const autonome = calcExterne === undefined && loadingExterne === undefined;
  const [calcLocal, setCalcLocal] = useState(null);
  const [loadingLocal, setLoadingLocal] = useState(false);

  const vendeuse = (() => {
    try { return isVendeuse(JSON.parse(localStorage.getItem('user') || '{}')?.role || ''); }
    catch { return false; }
  })();

  useEffect(() => {
    if (!autonome || !bijouId || vendeuse) return;
    let actif = true;
    setLoadingLocal(true);
    fetch(`${API_URL}/bijoux/${bijouId}/prix-calcule`, { headers: headers() })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (actif) setCalcLocal(d); })
      .catch(() => {})
      .finally(() => { if (actif) setLoadingLocal(false); });
    return () => { actif = false; };
  }, [bijouId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (vendeuse) return null; // §205

  const calc = autonome ? calcLocal : calcExterne;
  const loading = autonome ? loadingLocal : loadingExterne;

  if (loading) return <p className="text-sm text-gray-400">Calcul en cours…</p>;
  if (!calc) return <p className="text-sm text-gray-400">Détail indisponible.</p>;

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">
        Calculé au cours de fabrication : <strong>{calc.cours_or ? `${calc.cours_or} DH/g` : '—'}</strong>
        {calc.date_cours && <span> ({new Date(calc.date_cours).toLocaleDateString('fr-FR')})</span>}
      </p>
      <div className="divide-y divide-gray-100 text-sm">
        {[
          ['Métal', calc.pr?.metal],
          ['Perte métal (10 %)', calc.pr?.perte_metal],
          ['Pierres', calc.pr?.pierres],
          ['TVA pierres (20 %)', calc.pr?.tva_pierres],
          ['Façon', calc.pr?.facon],
          ['Options (fonte/rhodiage/garantie/gravure)', calc.pr?.options],
          ['Chaîne / mousqueton', calc.pr?.composants],
          ['Écrin', calc.pr?.ecrin],
        ].filter(([, v]) => v !== null && v !== undefined && v !== 0).map(([label, v]) => (
          <div key={label} className="flex justify-between py-1.5">
            <span className="text-gray-600">{label}</span>
            <span className="font-medium text-gray-800">{fmtDH(v)}</span>
          </div>
        ))}
        {calc.pr?.article_sup ? (
          <div className="flex justify-between py-1.5">
            <span className="text-gray-600">Article sup. <span className="text-xs text-gray-400">(hors PR, compté au PVP)</span></span>
            <span className="font-medium text-gray-800">{fmtDH(calc.pr.article_sup)}</span>
          </div>
        ) : null}
        <div className="flex justify-between py-2 font-bold text-gray-900">
          <span>PR total (coût de revient)</span>
          <span>{fmtDH(calc.pr?.total)}</span>
        </div>
      </div>
      <div className="bg-gray-50 rounded-lg p-3 space-y-1 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-600">
            PVP calculé
            <span className="text-xs text-gray-400 ml-1">
              ({calc.pvp?.mode === 'coef_global'
                ? `coef global ×${calc.coefs?.global}`
                : `coefs métal ×${calc.coefs?.metaux} · pierre ×${calc.coefs?.pierre} · façon ×${calc.coefs?.facon} · comp. ×${calc.coefs?.composant}`})
            </span>
          </span>
          <span className="font-bold text-gray-900">{fmtDH(calc.pvp?.total)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">PVP arrondi (centaine)</span>
          <span className="font-medium">{fmtDH(calc.pvp?.total_arrondi_centaine)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">Marge calculée</span>
          <span className={`font-bold ${(calc.marge || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>{fmtDH(calc.marge)}</span>
        </div>
        <div className="flex justify-between border-t border-gray-200 pt-1 mt-1">
          <span className="text-gray-600">Prix de vente figé (étiquette)</span>
          <span className="font-medium">{fmtDH(prixFige)}</span>
        </div>
      </div>
      {Array.isArray(calc.manques) && calc.manques.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
          <p className="text-xs font-bold text-orange-700 mb-1">⚠ Données manquantes pour un calcul complet</p>
          <ul className="text-xs text-orange-800 list-disc ml-4">
            {calc.manques.map((m, i) => <li key={i}>{m}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
